// Puente entre el backoffice y el manual de uso.
//
// El manual vive en el MISMO dominio que el portal, bajo /docs. Es un sitio
// estatico: no puede preguntarle a la API si quien entra tiene sesion. Lo
// resuelve un pase firmado que emite la API (services/admin-docs-ticket) y
// verifica una CloudFront Function en el borde.
//
// Este modulo hace tres cosas:
//   1. Guarda el pase en una cookie del dominio del portal.
//   2. Arma las URLs profundas (pagina + ancla) del manual.
//   3. Abre una pestaña nueva sin que el bloqueador de popups la corte.

import { adminDocsTicket } from '@/lib/api';

/**
 * Raiz publica del manual.
 *
 * En un deploy real es `/docs` sobre el mismo host, que es lo unico que hace
 * que la cookie del pase viaje. `VITE_DOCS_BASE` existe para el desarrollo
 * local, donde el manual corre en el servidor de Astro en otro puerto: ahi no
 * hay pase que valga, pero tampoco hay porton.
 */
const DOCS_BASE = (import.meta.env.VITE_DOCS_BASE as string | undefined) ?? '/docs';

/** Idioma con el que se abre el manual. El sitio tiene su propio selector. */
const DOCS_LANG = 'es';

const COOKIE_NAME = 'authsys_docs';

/**
 * Margen con el que se renueva el pase. Sin margen, una pestaña abierta justo
 * en el borde del vencimiento rebota al login sin motivo aparente.
 */
const REFRESH_MARGIN_SECONDS = 15 * 60;

function currentPassExpiry(): number | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`),
  );
  if (!match?.[1]) return null;
  // El pase es `<vencimiento>.<id>.<firma>`; el vencimiento se lee sin
  // verificar nada. La verificacion de verdad ocurre en el borde: aca solo
  // decidimos si conviene pedir uno nuevo.
  const seconds = Number.parseInt(decodeURIComponent(match[1]).split('.')[0] ?? '', 10);
  return Number.isFinite(seconds) ? seconds : null;
}

/**
 * Deja un pase vigente en la cookie. Devuelve `false` si no se pudo (sin
 * sesion, API caida): quien llama igual abre el manual, y el borde se encarga
 * de mandar al login.
 */
export async function ensureDocsPass(): Promise<boolean> {
  const now = Math.floor(Date.now() / 1000);
  const expiry = currentPassExpiry();
  if (expiry !== null && expiry > now + REFRESH_MARGIN_SECONDS) return true;

  try {
    const ticket = await adminDocsTicket();
    document.cookie = [
      `${ticket.cookie_name}=${ticket.value}`,
      'Path=/',
      `Max-Age=${Math.max(0, ticket.max_age)}`,
      'SameSite=Lax',
      // Sin HTTPS el navegador descarta una cookie con `Secure` sin decir nada,
      // y el desarrollo local queda sin manual por un motivo invisible.
      window.location.protocol === 'https:' ? 'Secure' : '',
    ]
      .filter(Boolean)
      .join('; ');
    return true;
  } catch {
    return false;
  }
}

/** Borra el pase. Se llama al cerrar sesion. */
export function clearDocsPass(): void {
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * URL de una pagina del manual. `page` es la ruta sin idioma ni barras
 * (`usuarios/ficha`); `anchor` es un ancla explicita declarada en el MDX con
 * `{#ancla}`, que es la misma en los dos idiomas a proposito.
 */
export function docsUrl(page?: string, anchor?: string): string {
  const path = page ? `${DOCS_LANG}/${page}/` : `${DOCS_LANG}/`;
  return `${DOCS_BASE}/${path}${anchor ? `#${anchor}` : ''}`;
}

/**
 * Abre el manual en una pestaña nueva.
 *
 * La pestaña se abre ANTES de pedir el pase, y despues se le cambia la
 * direccion. Al reves —esperar el `await` y recien ahi abrir— el navegador ya
 * no relaciona la ventana nueva con el clic del usuario y la bloquea.
 */
export async function openDocs(page?: string, anchor?: string): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;

  await ensureDocsPass();
  const url = docsUrl(page, anchor);

  if (tab) tab.location.replace(url);
  else window.open(url, '_blank', 'noopener');
}
